import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Calendar, Target, Rocket, CheckCircle2, 
  Loader2, ChevronDown, ChevronUp, Clock, Sparkles,
  Flag, Trophy, BookOpen, Download, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Plan90 = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expandedWeek, setExpandedWeek] = useState(1);
  const [completedTasks, setCompletedTasks] = useState({});

  const fetchPlan = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/plan90`);
      setPlan(response.data.plan);
    } catch (error) {
      if (error.response?.status === 404) {
        setPlan(null);
      } else if (error.response?.status === 402) {
        toast.error(language === 'fr' ? 'Achetez le Plan 90 jours pour y accéder' : 'Purchase the 90-day plan to access');
        navigate('/payment');
      }
    } finally {
      setLoading(false);
    }
  }, [language, navigate]);

  const fetchTaskProgress = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/plan90/task-progress`);
      const serverTasks = response.data.completed_tasks || {};
      // Convert server format (key: date) to boolean format
      const boolTasks = {};
      Object.keys(serverTasks).forEach(key => { boolTasks[key] = true; });
      
      // Merge with localStorage (migration)
      const saved = localStorage.getItem(`plan90_progress_${user?.id}`);
      if (saved) {
        const localTasks = JSON.parse(saved);
        Object.keys(localTasks).forEach(key => {
          if (localTasks[key] && !boolTasks[key]) {
            boolTasks[key] = true;
            // Sync local to server
            axios.post(`${API}/plan90/complete-task`, { task_key: key, completed: true }).catch(() => {});
          }
        });
        // Clear localStorage after migration
        localStorage.removeItem(`plan90_progress_${user?.id}`);
      }
      
      setCompletedTasks(boolTasks);
    } catch (error) {
      // Fallback to localStorage
      const saved = localStorage.getItem(`plan90_progress_${user?.id}`);
      if (saved) setCompletedTasks(JSON.parse(saved));
    }
  }, [user?.id]);

  useEffect(() => {
    fetchPlan();
    fetchTaskProgress();
  }, [fetchPlan, fetchTaskProgress]);

  const generatePlan = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/plan90/generate`, {}, { timeout: 180000 }); // 3 minutes timeout
      if (response.data && response.data.plan) {
        setPlan(response.data.plan);
        toast.success(language === 'fr' ? 'Plan généré avec succès !' : 'Plan generated successfully!');
      } else {
        toast.error(language === 'fr' ? 'Réponse invalide du serveur' : 'Invalid server response');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || (language === 'fr' ? 'Erreur de génération' : 'Generation failed');
      toast.error(errorMsg);
      
      // If timeout, show specific message
      if (error.code === 'ECONNABORTED') {
        toast.error(language === 'fr' ? 'La génération prend trop de temps. Réessayez.' : 'Generation taking too long. Please retry.');
      }
    } finally {
      setGenerating(false);
    }
  };

  const toggleTask = async (weekNum, taskIndex) => {
    const key = `${weekNum}-${taskIndex}`;
    const newCompleted = !completedTasks[key];
    setCompletedTasks(prev => ({ ...prev, [key]: newCompleted }));
    
    try {
      await axios.post(`${API}/plan90/complete-task`, { task_key: key, completed: newCompleted });
    } catch (e) {
      console.warn('Task sync failed:', e.message);
    }
  };

  const getWeekProgress = (weekNum, tasks) => {
    if (!tasks || tasks.length === 0) return 0;
    const completed = tasks.filter((_, i) => completedTasks[`${weekNum}-${i}`]).length;
    return Math.round((completed / tasks.length) * 100);
  };

  const getTotalProgress = () => {
    let weeks = plan?.weeks || [];
    // Handle phases structure
    if (!weeks.length && plan?.phases) {
      plan.phases.forEach(phase => { weeks = weeks.concat(phase.weeks || []); });
    }
    if (!weeks.length) return 0;
    let totalTasks = 0;
    let completedCount = 0;
    weeks.forEach((week) => {
      const weekNum = week.week_number || week.week || 0;
      (week.tasks || []).forEach((_, taskIndex) => {
        totalTasks++;
        if (completedTasks[`${weekNum}-${taskIndex}`]) completedCount++;
      });
    });
    return totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;
  };

  const getPhaseColor = (weekNum) => {
    if (weekNum <= 4) return '#FF6B6B'; // Exploration
    if (weekNum <= 8) return '#FFE66D'; // Construction
    return '#4ECDC4'; // Lancement
  };

  const getPhaseLabel = (weekNum) => {
    if (weekNum <= 4) return language === 'fr' ? 'Exploration' : 'Exploration';
    if (weekNum <= 8) return language === 'fr' ? 'Construction' : 'Building';
    return language === 'fr' ? 'Lancement' : 'Launch';
  };

  const handleExportPDF = async () => {
    if (!plan) return;
    
    setExporting(true);
    toast.info(language === 'fr' ? 'Génération du PDF en cours...' : 'Generating PDF...');

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPosition = margin;

      // Header with gradient effect
      pdf.setFillColor(78, 205, 196);
      pdf.rect(0, 0, pageWidth, 45, 'F');
      
      // Title
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      pdf.text(plan.plan_title || (language === 'fr' ? 'Plan d\'Action 90 Jours' : '90-Day Action Plan'), pageWidth / 2, 18, { align: 'center' });
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`${user?.name || 'Utilisateur'} - ${new Date().toLocaleDateString('fr-FR')}`, pageWidth / 2, 28, { align: 'center' });
      
      // Progress
      const totalProgress = getTotalProgress();
      pdf.setFontSize(10);
      pdf.text(`${language === 'fr' ? 'Progression' : 'Progress'}: ${totalProgress}%`, pageWidth / 2, 38, { align: 'center' });

      yPosition = 55;

      // Plan Summary
      if (plan.plan_summary) {
        pdf.setTextColor(50, 50, 50);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'italic');
        const summaryLines = pdf.splitTextToSize(plan.plan_summary, pageWidth - 2 * margin);
        pdf.text(summaryLines, margin, yPosition);
        yPosition += summaryLines.length * 5 + 10;
      }

      // Weeks
      plan.weeks?.forEach((week, index) => {
        const weekNum = week.week_number || index + 1;
        const phaseColor = weekNum <= 4 ? [255, 107, 107] : weekNum <= 8 ? [255, 230, 109] : [78, 205, 196];
        
        // Check if we need a new page
        if (yPosition > pageHeight - 80) {
          pdf.addPage();
          yPosition = margin;
        }

        // Week header
        pdf.setFillColor(...phaseColor);
        pdf.circle(margin + 4, yPosition + 2, 4, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.text(String(weekNum), margin + 4, yPosition + 4, { align: 'center' });

        pdf.setTextColor(50, 50, 50);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${language === 'fr' ? 'Semaine' : 'Week'} ${weekNum}: ${week.theme}`, margin + 12, yPosition + 4);
        yPosition += 10;

        // Objective
        if (week.objective) {
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'italic');
          pdf.setTextColor(100, 100, 100);
          const objLines = pdf.splitTextToSize(`${language === 'fr' ? 'Objectif' : 'Objective'}: ${week.objective}`, pageWidth - 2 * margin - 12);
          pdf.text(objLines, margin + 12, yPosition);
          yPosition += objLines.length * 4 + 3;
        }

        // Tasks
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(60, 60, 60);
        pdf.setFontSize(9);
        week.tasks?.forEach((task, taskIndex) => {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = margin;
          }
          
          const isCompleted = completedTasks[`${weekNum}-${taskIndex}`];
          const taskText = task.task || task;
          
          // Checkbox
          if (isCompleted) {
            pdf.setFillColor(...phaseColor);
            pdf.rect(margin + 12, yPosition - 2.5, 3, 3, 'F');
          } else {
            pdf.setDrawColor(...phaseColor);
            pdf.rect(margin + 12, yPosition - 2.5, 3, 3, 'S');
          }
          
          const taskLines = pdf.splitTextToSize(taskText, pageWidth - 2 * margin - 25);
          pdf.text(taskLines, margin + 18, yPosition);
          yPosition += taskLines.length * 4 + 2;
        });

        // Milestone
        if (week.milestone) {
          pdf.setFillColor(255, 193, 7);
          pdf.rect(margin + 12, yPosition, 2, 4, 'F');
          pdf.setFontSize(8);
          pdf.setTextColor(150, 120, 0);
          const milestoneLines = pdf.splitTextToSize(`${language === 'fr' ? 'Jalon' : 'Milestone'}: ${week.milestone}`, pageWidth - 2 * margin - 20);
          pdf.text(milestoneLines, margin + 17, yPosition + 3);
          yPosition += milestoneLines.length * 3.5 + 5;
        }

        yPosition += 6;
      });

      // Final Goal
      if (plan.final_goal) {
        if (yPosition > pageHeight - 40) {
          pdf.addPage();
          yPosition = margin;
        }
        
        pdf.setFillColor(78, 205, 196);
        pdf.roundedRect(margin, yPosition, pageWidth - 2 * margin, 25, 3, 3, 'F');
        
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text(language === 'fr' ? 'Objectif Final' : 'Final Goal', margin + 5, yPosition + 7);
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        const goalLines = pdf.splitTextToSize(plan.final_goal, pageWidth - 2 * margin - 10);
        pdf.text(goalLines, margin + 5, yPosition + 14);
      }

      // Footer
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(
        `Généré par Ikigai App - ${new Date().toLocaleDateString('fr-FR')}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );

      // Save
      pdf.save(`plan-90-jours-${user?.name || 'user'}.pdf`);
      toast.success(language === 'fr' ? 'PDF téléchargé !' : 'PDF downloaded!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de l\'export' : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#4ECDC4]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors"
            data-testid="back-to-dashboard-plan90"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#4ECDC4]" />
            <span className="font-serif font-semibold text-stone-800">
              {language === 'fr' ? 'Plan 90 Jours' : '90-Day Plan'}
            </span>
          </div>
          
          {plan ? (
            <Button
              onClick={handleExportPDF}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              disabled={exporting}
              data-testid="export-pdf-plan90-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">PDF</span>
            </Button>
          ) : (
            <div className="w-10" />
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {!plan ? (
          /* No plan yet - Generate CTA */
          <div className="text-center py-12">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#4ECDC4] to-[#1A535C] flex items-center justify-center mx-auto mb-6">
              <Rocket className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-serif text-3xl font-semibold text-stone-900 mb-3">
              {language === 'fr' ? 'Génère ton Plan d\'Action' : 'Generate Your Action Plan'}
            </h1>
            <p className="text-stone-600 mb-8 max-w-md mx-auto">
              {language === 'fr' 
                ? 'Basé sur ton profil Ikigai, l\'IA va créer un plan personnalisé de 12 semaines pour concrétiser ta vocation.'
                : 'Based on your Ikigai profile, AI will create a personalized 12-week plan to achieve your calling.'}
            </p>
            <Button
              onClick={generatePlan}
              className="bg-[#4ECDC4] hover:bg-[#3DBDB5] text-white rounded-xl h-12 px-8 font-medium"
              disabled={generating}
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  {language === 'fr' ? 'Génération en cours... (30-60s)' : 'Generating... (30-60s)'}
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  {language === 'fr' ? 'Générer mon plan' : 'Generate my plan'}
                </>
              )}
            </Button>
          </div>
        ) : (
          /* Plan exists - Display it */
          <>
            {/* Plan Header */}
            <div className="bg-gradient-to-br from-[#4ECDC4]/10 to-[#1A535C]/10 rounded-3xl p-6 mb-6">
              <h1 className="font-serif text-2xl font-semibold text-stone-900 mb-2">
                {plan.plan_title}
              </h1>
              <p className="text-stone-600 mb-4">{plan.plan_summary}</p>
              
              {/* Progress */}
              <div className="bg-white rounded-2xl p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-stone-600">
                    {language === 'fr' ? 'Progression globale' : 'Overall progress'}
                  </span>
                  <span className="font-serif text-xl font-bold text-[#4ECDC4]">
                    {getTotalProgress()}%
                  </span>
                </div>
                <Progress value={getTotalProgress()} className="h-3" />
              </div>
            </div>

            {/* Phase indicators */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[
                { label: language === 'fr' ? 'Exploration' : 'Exploration', weeks: '1-4', color: '#FF6B6B', icon: Target },
                { label: language === 'fr' ? 'Construction' : 'Building', weeks: '5-8', color: '#FFE66D', icon: BookOpen },
                { label: language === 'fr' ? 'Lancement' : 'Launch', weeks: '9-12', color: '#4ECDC4', icon: Rocket }
              ].map((phase, i) => (
                <div 
                  key={`k-${i}`}
                  className="bg-white rounded-xl p-3 text-center border-2"
                  style={{ borderColor: `${phase.color}30` }}
                >
                  <phase.icon className="w-5 h-5 mx-auto mb-1" style={{ color: phase.color }} />
                  <p className="text-xs font-semibold text-stone-800">{phase.label}</p>
                  <p className="text-xs text-stone-500">{language === 'fr' ? 'Sem.' : 'Week'} {phase.weeks}</p>
                </div>
              ))}
            </div>

            {/* Weeks */}
            <div className="space-y-3">
              {(() => {
                // Handle both weeks[] and phases[].weeks[] structures
                let allWeeks = plan.weeks || [];
                if (!allWeeks.length && plan.phases) {
                  plan.phases.forEach(phase => { allWeeks = allWeeks.concat(phase.weeks || []); });
                }
                return allWeeks;
              })().map((week, index) => {
                const weekNum = week.week_number || week.week || index + 1;
                const isExpanded = expandedWeek === weekNum;
                const phaseColor = getPhaseColor(weekNum);
                const progress = getWeekProgress(weekNum, week.tasks);
                
                return (
                  <div 
                    key={weekNum}
                    className="bg-white rounded-2xl border border-stone-100 overflow-hidden"
                  >
                    {/* Week header */}
                    <button
                      onClick={() => setExpandedWeek(isExpanded ? null : weekNum)}
                      className="w-full p-4 flex items-center justify-between hover:bg-stone-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                          style={{ backgroundColor: phaseColor }}
                        >
                          {weekNum}
                        </div>
                        <div className="text-left">
                          <p className="font-semibold text-stone-900 text-sm">{week.theme || week.title}</p>
                          <p className="text-xs text-stone-500">{getPhaseLabel(weekNum)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <span className="text-sm font-medium" style={{ color: phaseColor }}>
                            {progress}%
                          </span>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-stone-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-stone-400" />
                        )}
                      </div>
                    </button>
                    
                    {/* Week content */}
                    {isExpanded && (
                      <div className="px-4 pb-4 border-t border-stone-100">
                        {/* Objective */}
                        <div className="py-3 border-b border-stone-50">
                          <p className="text-xs text-stone-500 mb-1">
                            {language === 'fr' ? 'Objectif' : 'Objective'}
                          </p>
                          <p className="text-sm text-stone-700">{week.objective}</p>
                        </div>
                        
                        {/* Tasks */}
                        <div className="py-3">
                          <p className="text-xs text-stone-500 mb-2">
                            {language === 'fr' ? 'Tâches' : 'Tasks'}
                          </p>
                          <div className="space-y-2">
                            {week.tasks?.map((task, taskIndex) => (
                              <div 
                                key={taskIndex}
                                onClick={() => toggleTask(weekNum, taskIndex)}
                                className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                                  completedTasks[`${weekNum}-${taskIndex}`]
                                    ? 'bg-green-50 border border-green-200'
                                    : 'bg-stone-50 hover:bg-stone-100'
                                }`}
                              >
                                <CheckCircle2 
                                  className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                                    completedTasks[`${weekNum}-${taskIndex}`]
                                      ? 'text-green-500'
                                      : 'text-stone-300'
                                  }`}
                                />
                                <div className="flex-1">
                                  <p className={`text-sm ${
                                    completedTasks[`${weekNum}-${taskIndex}`]
                                      ? 'text-stone-500 line-through'
                                      : 'text-stone-700'
                                  }`}>
                                    {typeof task === 'string' ? task : task.task}
                                  </p>
                                  <div className="flex items-center gap-2 mt-1">
                                    {task.day && (
                                      <span className="text-xs text-stone-400">{task.day}</span>
                                    )}
                                    {task.duration && (
                                      <span className="flex items-center gap-1 text-xs text-stone-400">
                                        <Clock className="w-3 h-3" />
                                        {task.duration}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Milestone */}
                        {week.milestone && (
                          <div className="pt-3 border-t border-stone-50">
                            <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-xl">
                              <Flag className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-xs font-medium text-amber-800">
                                  {language === 'fr' ? 'Jalon' : 'Milestone'}
                                </p>
                                <p className="text-sm text-amber-700">{week.milestone}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Final Goal */}
            {plan.final_goal && (
              <div className="mt-6 bg-gradient-to-br from-[#4ECDC4] to-[#1A535C] rounded-3xl p-6 text-white">
                <div className="flex items-center gap-3 mb-3">
                  <Trophy className="w-6 h-6" />
                  <h3 className="font-serif text-lg font-semibold">
                    {language === 'fr' ? 'Objectif Final' : 'Final Goal'}
                  </h3>
                </div>
                <p className="text-white/90">{plan.final_goal}</p>
              </div>
            )}

            {/* Resources */}
            {plan.resources && plan.resources.length > 0 && (
              <div className="mt-6 bg-white rounded-2xl p-5 border border-stone-100">
                <h3 className="font-semibold text-stone-900 mb-3 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-[#4ECDC4]" />
                  {language === 'fr' ? 'Ressources utiles' : 'Useful resources'}
                </h3>
                <ul className="space-y-2">
                  {plan.resources.map((resource, i) => (
                    <li key={`k-${i}`} className="flex items-start gap-2 text-sm text-stone-600">
                      <span className="text-[#4ECDC4]">•</span>
                      {resource}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default Plan90;
